import type { MaybeComputedElementRef, MaybeElement } from '@vueuse/core';
import type { Panel } from '#layers/ui/types/panel';
import { useElementSize, useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, markRaw, reactive, ref, watch } from 'vue';
import { toSortedArray, workspaceScopedKey } from '#layers/base/public';
import { useSplitPanelDrawer } from '#layers/ui/composables/use-split-panel-drawer';

export const SIDEBAR_MIN_SIZE = 220;
export const SIDEBAR_DEFAULT_SIZE = 290;
export const SIDEBAR_MAX_SIZE = 360;

export const PANEL_MIN_SIZE = 220;
export const PANEL_DEFAULT_SIZE = 360;
export const PANEL_MAX_SIZE = 500;

export const CONTENT_MIN_SIZE = 550;

type PaneId = 'panel' | 'sidebar';

export const usePaneStore = defineStore('workspace-pane-store', () => {
	const registeredPanels = reactive(new Map<string, Panel>());
	const currentPanelId = useLocalStorage<string | null>(workspaceScopedKey('workspace-panel-current'), null);

	const registerPanel = (panel: Panel) => {
		const registeredPanel: Panel = {
			...panel,
			component: markRaw(panel.component),
		};

		if (panel.toggleComponent) {
			registeredPanel.toggleComponent = markRaw(panel.toggleComponent);
		}

		registeredPanels.set(panel.id, registeredPanel);
	};

	const unregisterPanel = (panel: Panel) => {
		registeredPanels.delete(panel.id);
	};

	const panels = computed(() => {
		return toSortedArray([...registeredPanels.values()], 'order');
	});

	const currentPanel = computed<Panel | null>(() => {
		const selectedPanel = currentPanelId.value ? registeredPanels.get(currentPanelId.value) : undefined;

		return selectedPanel ?? panels.value[0] ?? null;
	});

	const availablePaneWidth = ref<number>(-1);

	const syncAvailablePaneWidth = (el: MaybeComputedElementRef<MaybeElement>) => {
		const { width: elementWidth } = useElementSize(el);

		watch(elementWidth, (width) => {
			if (width <= 0 && availablePaneWidth.value > 0) return;

			availablePaneWidth.value = width;
		}, { immediate: true });
	};

	const panelRoom = computed(() => {
		if (availablePaneWidth.value > CONTENT_MIN_SIZE + SIDEBAR_MAX_SIZE + PANEL_MAX_SIZE) return 2;
		if (availablePaneWidth.value > CONTENT_MIN_SIZE + Math.max(SIDEBAR_MAX_SIZE, PANEL_MAX_SIZE)) return 1;
		return 0;
	});

	const sidebarVisibleState = useLocalStorage(workspaceScopedKey('workspace-sidebar-visible'), true);
	const sidebarSize = useLocalStorage(workspaceScopedKey('workspace-sidebar-size'), SIDEBAR_DEFAULT_SIZE);

	const panelVisibleState = useLocalStorage(workspaceScopedKey('workspace-panel-visible'), false);
	const panelSize = useLocalStorage(workspaceScopedKey('workspace-panel-size'), PANEL_DEFAULT_SIZE);

	const lastOpenedPane = ref<PaneId | null>(null);

	watch(sidebarSize, (newSize) => {
		sidebarSize.value = Math.max(SIDEBAR_MIN_SIZE, Math.min(newSize, SIDEBAR_MAX_SIZE));
	}, { immediate: true });

	watch(panelSize, (newSize) => {
		panelSize.value = Math.max(PANEL_MIN_SIZE, Math.min(newSize, PANEL_MAX_SIZE));
	}, { immediate: true });

	const getPaneVisibleState = (pane: PaneId) => {
		return pane === 'panel' ? panelVisibleState : sidebarVisibleState;
	};

	const getOtherPane = (pane: PaneId): PaneId => {
		return pane === 'panel' ? 'sidebar' : 'panel';
	};

	const setPaneVisible = (pane: PaneId, visible: boolean) => {
		const paneVisible = getPaneVisibleState(pane);

		if (paneVisible.value === visible) return;

		paneVisible.value = visible;

		if (visible) {
			lastOpenedPane.value = pane;

			if (panelRoom.value === 1) {
				getPaneVisibleState(getOtherPane(pane)).value = false;
			}
		}
		else if (lastOpenedPane.value === pane) {
			lastOpenedPane.value = null;
		}
	};

	const sidebarVisible = useVisible('sidebar');
	const panelVisible = useVisible('panel');

	const sidebarUseSplitPanel = computed(() => panelRoom.value > 0);

	const panelUseSplitPanel = computed(() => {
		return currentPanel.value !== null
			&& panelRoom.value > 0;
	});

	const sidebarPane = useSplitPanelDrawer({ split: sidebarUseSplitPanel, visible: sidebarVisible });
	const panelPane = useSplitPanelDrawer({ split: panelUseSplitPanel, visible: panelVisible });

	watch(panelRoom, (room) => {
		if (room !== 1) return;
		if (!panelVisibleState.value || !sidebarVisibleState.value) return;

		setPaneVisible(lastOpenedPane.value === 'sidebar' ? 'panel' : 'sidebar', false);
	});

	const toggleSidebar = (open?: boolean) => {
		sidebarPane.toggle(open);
	};

	const togglePanel = (opts?: { panelId?: string; open?: boolean }) => {
		const panelIdChanged = !!opts?.panelId && opts.panelId !== currentPanel.value?.id;

		if (opts?.panelId) {
			currentPanelId.value = opts.panelId;
		}

		// Switching to another panel shows it rather than toggling the pane closed
		panelPane.toggle(panelIdChanged ? (opts?.open ?? true) : opts?.open);
	};

	function useVisible(pane: PaneId) {
		return computed({
			get: () => getPaneVisibleState(pane).value,
			set: (visible: boolean) => setPaneVisible(pane, visible),
		});
	}

	return {
		availablePaneWidth,
		syncAvailablePaneWidth,
		sidebarVisible,
		sidebarOpen: sidebarPane.open,
		sidebarDrawerVisible: sidebarPane.drawerVisible,
		sidebarSize,
		toggleSidebar,
		sidebarUseSplitPanel,
		sidebarSplitPanelCollapsed: sidebarPane.splitPanelCollapsed,
		panelVisible,
		panelOpen: panelPane.open,
		panelDrawerVisible: panelPane.drawerVisible,
		panelSize,
		togglePanel,
		panelUseSplitPanel,
		panelSplitPanelCollapsed: panelPane.splitPanelCollapsed,
		currentPanelId,
		currentPanel,
		registerPanel,
		unregisterPanel,
		panels,
	};
});
